import Container from "react-bootstrap/Container";
import React, { useState, useEffect } from "react";
import { jmolColors, covalentRadius } from "../atomicData";
import Chart from "chart.js/auto";
import { BindingEnergyDataset, ChargeDataset } from "../Datasets.js";
import { getPath, collectRelated } from "../utils";
import { AtomStructure, Atom } from "../AtomicStructure";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import Stack from "react-bootstrap/Stack";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";

// Placeholder X3D view
const DataPlot = ({
  plotId,
  viewType,
  xAxisTitle,
  yAxisTitle,
  currentData,
  relatedData,
}) => {
  // Pull structure from adsorbed state data
  // (currently in POSCAR format)  TODO: use better standard
  function getStructureData() {
    return getPath(
      [viewType, "dft_results", "dft_physical_structure"],
      currentData,
    );
  }

  // Get similarity between current calculation and related calcs
  function getDistances() {
    var structureData = getStructureData();
    var thisStructure = new AtomStructure(structureData);
    var relatedStructureData = relatedData.map((system, i) => {
      return new AtomStructure(
        getPath([viewType, "dft_results", "dft_physical_structure"], system),
      );
    });
    return relatedStructureData.map((structure, i) => {
      return thisStructure.similarity(structure);
    });
  }

  // Create an empty Chart.js plot (by potential)
  function makeEmptyPlot() {
    (async function () {
      new Chart(document.getElementById(plotId), {
        type: "scatter",
        options: {
          maintainAspectRatio: false,
          animation: {
            duration: 500,
          },
          responsive: true,
          maintainAspectRatio: false,
          elements: {
            point: {
              borderWidth: 1,
              radius: 5,
              backgroundColor: "rgba(0,0,0,0)",
            },
          },
          scales: {
            y: {
              title: {
                display: true,
                text: yAxisTitle,
              },
            },
            x: {
              title: {
                display: true,
                text: "Applied Potential (eV)",
              },
            },
          },
          plugins: {
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                title: () => null,
              },
            },
          },
        },
      });
    })();
  }

  // Create plot with the specified datasets
  function makePlot(datasets, labels, yAxisTitle) {
    let existingChart = Chart.getChart(plotId);
    existingChart.data.datasets.forEach((dataset) => {
      dataset.data = [];
    });
    existingChart.data.labels = [];
    if (labels !== null) {
      existingChart.data.labels = labels;
    }
    existingChart.data.datasets = datasets;
    existingChart.options.scales.y.title.text = yAxisTitle;
    existingChart.update();
  }

  // Called by dropdown to create adsorption energy or charge plot
  function plotValue(key, name, type) {
    var potentials = collectRelated(relatedData, [
      viewType,
      "dft_solvation_expparams",
      "applied_potential",
    ]);

    function onlyUnique(value, index, array) {
      return array.indexOf(value) === index;
    }

    var uniquePotentials = potentials
      .filter((potential) => potential !== null)
      .filter(onlyUnique);

    // Organize related data and sort by potential
    var structureDistances = getDistances();

    var dataDistances = [];
    for (var i = 0; i < structureDistances.length; i++) {
      dataDistances.push({
        distance: structureDistances[i],
        chargeData: relatedData[i],
        potential: potentials[i],
      });
    }

    dataDistances.sort((a, b) => {
      return a.distance < b.distance ? -1 : a.distance == b.distance ? 0 : 1;
    });

    var dataDifferentSite = dataDistances;
    var dataSameSite = [];
    uniquePotentials.forEach((potential) => {
      for (i in dataDifferentSite) {
        if (dataDifferentSite[i].potential == potential) {
          dataSameSite.push(dataDistances[i]);
          dataDifferentSite.splice(i, 1);
          break;
        }
      }
    });

    dataSameSite.sort((a, b) => {
      return a.potential < b.potential
        ? -1
        : a.potential == b.potential
          ? 0
          : 1;
    });

    let existingChart = Chart.getChart(plotId);
    if (existingChart?.canvas !== undefined) {
      existingChart.destroy();
    }

    // Select plot type and create Chart.js datasets
    if (type === "charge") {
      var datasets = [
        new ChargeDataset(
          dataSameSite.map((i) => {
            return i.potential;
          }),
          collectRelated(dataSameSite, [
            "chargeData",
            viewType,
            "oxidation_states",
            key,
          ]),
          collectRelated(dataSameSite, ["chargeData", "unique_id"]),
        ),
      ];

      makeEmptyPlot();

      var plottingDatasets = [
        {
          label: name,
          data: datasets[0].toPlotDataPoints(),
          borderColor: datasets[0].color,
          backgroundColor: datasets[0].color,
          showLine: true,
          tension: 0.1,
        },
      ];
      var labels = datasets[0].labels;
      var yAxisTitle = "Charge State";
    } else if (type == "total_charge") {
      var datasets = [
        new ChargeDataset(
          dataSameSite.map((i) => {
            return i.potential;
          }),
          collectRelated(dataSameSite, [
            "chargeData",
            viewType,
            "dft_results",
            "net_charge_state",
          ]),
          collectRelated(dataSameSite, ["chargeData", "unique_id"]),
        ),
      ];

      makeEmptyPlot();

      var plottingDatasets = [
        {
          label: name,
          data: datasets[0].toPlotDataPoints(),
          borderColor: datasets[0].color,
          backgroundColor: datasets[0].color,
          showLine: true,
          tension: 0.1,
        },
      ];
      var labels = datasets[0].labels;
      var yAxisTitle = "Charge State";
    } else if (type == "adsorption_energy") {
      var datasets = [
        new BindingEnergyDataset(
          dataSameSite.map((i) => {
            return i.potential;
          }),
          collectRelated(dataSameSite, [
            "chargeData",
            viewType,
            "dft_results",
            "adsorption_energy",
          ]),
          collectRelated(dataSameSite, ["chargeData", "unique_id"]),
        ),
        new BindingEnergyDataset(
          dataDifferentSite.map((i) => {
            return i.potential;
          }),
          collectRelated(dataDifferentSite, [
            "chargeData",
            viewType,
            "dft_results",
            "adsorption_energy",
          ]),
          collectRelated(dataDifferentSite, ["chargeData", "unique_id"]),
        ),
      ];

      makeEmptyPlot();

      var plottingDatasets = datasets.map((dataset, i) => {
        return {
          label: i === 0 ? "Same Active Site" : "Different Active Site",
          labels: dataset.labels,
          data: dataset.toPlotDataPoints(),
          borderColor: i === 0 ? dataset.color : "rgb(255, 195, 0)",
          backgroundColor: i === 0 ? dataset.color : "rgb(255, 195, 0)",
          showLine: i === 0 ? true : false,
          tension: 0.1,
        };
      });
      labels = null;
      var yAxisTitle = "Adsorption Energy (eV)";
    }

    makePlot(plottingDatasets, labels, yAxisTitle);
  }

  // Get site labels for structure
  function getLabels() {
    var structure = new AtomStructure(getStructureData());

    var labels = [];
    structure.species.forEach((elem) => {
      for (var i = 0; i < elem[1]; i++) {
        labels.push(elem[0]);
      }
    });
    return labels;
  }

  // Load Chart.js and make initial plot
  useEffect(() => {
    let existingChargeChart = Chart.getChart(plotId);
    if (existingChargeChart?.canvas !== undefined) {
      existingChargeChart.destroy();
    }

    var label = "";

    if (viewType === "adsorbed_state") {
      plotValue(-2, label, "adsorption_energy");
    } else if (viewType === "catalyst") {
      plotValue(0, "Total Charge", "total_charge");
    }

    return;
  }, []);

  var handleSelect = (key, siteName, type) => {
    plotValue(key, siteName, type);
  };

  var labels = getLabels();

  var activeSiteIndices = getPath(
    [viewType, "active_site_indices"],
    currentData,
  );
  var adsorbateIndices = getPath([viewType, "adsorbate_indices"], currentData);
  var sites = activeSiteIndices
    ? activeSiteIndices.concat(adsorbateIndices)
    : [];

  // Create dropdown for plot options
  function getDropdowns() {
    var dropdowns = [];
    if (viewType === "adsorbed_state") {
      dropdowns.push(
        <Dropdown.Item
          onClick={() =>
            handleSelect(0, "Adsorption Energy", "adsorption_energy")
          }
          key={-4}
        >
          {"Adsorption Energy"}
        </Dropdown.Item>,
      );

      dropdowns.push(<Dropdown.Divider key={-3} />);
    }

    dropdowns = dropdowns.concat([
      <Dropdown.Item
        onClick={() => handleSelect(0, "Total Charge", "total_charge")}
        key={-2}
      >
        {"Total Charge"}
      </Dropdown.Item>,
    ]);

    if (viewType === "adsorbed_state") {
      dropdowns.push(<Dropdown.Divider key={-1} />);

      dropdowns = dropdowns.concat(
        sites.map((val, index) => {
          return (
            <Dropdown.Item
              onClick={() =>
                handleSelect(val, labels[val] + " " + val, "charge")
              }
              key={val}
            >
              {labels[val] + " " + val + " Charge"}
            </Dropdown.Item>
          );
        }),
      );
    }
    return dropdowns;
  }

  // Handle clicking on plot (linking to other calcs)
  function chargeChartClickHandler(evt) {
    let existingChart = Chart.getChart(plotId);
    const points = existingChart.getElementsAtEventForMode(
      evt,
      "nearest",
      { intersect: true },
      true,
    );

    if (points.length) {
      var firstPoint = points[0];
      var label = existingChart.data.labels[firstPoint.index];
      let linkPath = "";
      if (viewType === "adsorbed_state") {
        linkPath = "/adsorbed_states/";
      } else if (viewType === "catalyst") {
        linkPath = "/catalysts/";
      }
      if (label) {
        window.location.href = linkPath + label;
        return;
      }
      // Adsorbed state plots require slightly different logic...
      else {
        var label =
          existingChart.data.datasets[firstPoint.datasetIndex].labels[
            firstPoint.index
          ];
        window.location.href = linkPath + label;
      }
    }
  }

  return (
    <Stack>
      <Row>
        <Col></Col>
        <Col xs={6} sm={6} lg={6}>
          <Card.Title className="mx-4 my-4">Potential Plots</Card.Title>
        </Col>
        <Col
          xs={4}
          sm={3}
          md={4}
          lg={3}
          className="px-4 py-4"
          style={{ display: "flex", justifyContent: "right" }}
        >
          <DropdownButton
            title="Select Plot"
            style={{ display: "flex", justifyContent: "right" }}
          >
            {getDropdowns()}
          </DropdownButton>
        </Col>
      </Row>
      <Container>
        <canvas
          height="300px"
          id={plotId}
          onClick={chargeChartClickHandler}
        ></canvas>
      </Container>
    </Stack>
  );
};

export { DataPlot };
